// Working-draft legal copy for KRV Nexus, prepared for compliance review.

const UPDATED = "June 2026";

export const LEGAL_DOCS = {
  privacy: {
    title: "Privacy Policy",
    updated: UPDATED,
    sections: [
      {
        heading: "1. Introduction",
        body: [
          "KRV Nexus Pvt Ltd (\"KRV Nexus\", \"we\", \"us\", or \"our\") respects your privacy and is committed to protecting the personal information you share with us. This Privacy Policy explains how we collect, use, disclose, and safeguard information when you visit our website or interact with us.",
          "By using this website, you consent to the practices described in this policy.",
        ],
      },
      {
        heading: "2. Information We Collect",
        body: ["We may collect the following categories of information:"],
        list: [
          "Contact details you voluntarily provide through our enquiry forms, such as name, email address, phone number, and organization.",
          "The content of any message or enquiry you submit to us.",
          "Technical information such as browser type, device information, and usage data collected through cookies and similar technologies.",
        ],
      },
      {
        heading: "3. How We Use Your Information",
        body: ["We use the information we collect to:"],
        list: [
          "Respond to your enquiries and communicate with you regarding business, vendor, partnership, career, or media interests.",
          "Operate, maintain, and improve our website and services.",
          "Comply with applicable legal and regulatory obligations.",
        ],
      },
      {
        heading: "4. Sharing of Information",
        body: [
          "We do not sell your personal information. We may share information with trusted service providers who support our operations, or where required by law, regulation, or legal process. Any such sharing is limited to what is necessary for the stated purpose.",
        ],
      },
      {
        heading: "5. Data Security & Retention",
        body: [
          "We implement reasonable technical and organizational measures to protect your information against unauthorized access, alteration, or disclosure. We retain personal information only for as long as necessary to fulfil the purposes described in this policy or as required by law.",
        ],
      },
      {
        heading: "6. Your Rights",
        body: [
          "Subject to applicable law, you may request access to, correction of, or deletion of your personal information. To exercise these rights, please contact us using the details provided on our Contact page.",
        ],
      },
      {
        heading: "7. Changes to This Policy",
        body: [
          "We may update this Privacy Policy from time to time. Updates will be posted on this page with a revised \"last updated\" date.",
        ],
      },
    ],
  },

  terms: {
    title: "Terms of Use",
    updated: UPDATED,
    sections: [
      {
        heading: "1. Acceptance of Terms",
        body: [
          "These Terms of Use govern your access to and use of the KRV Nexus Pvt Ltd website. By accessing or using this website, you agree to be bound by these Terms. If you do not agree, please do not use the website.",
        ],
      },
      {
        heading: "2. Use of the Website",
        body: ["You agree to use this website only for lawful purposes. You must not:"],
        list: [
          "Use the website in any way that breaches applicable laws or regulations.",
          "Attempt to gain unauthorized access to any part of the website or its systems.",
          "Introduce malicious code or interfere with the proper functioning of the website.",
        ],
      },
      {
        heading: "3. Intellectual Property",
        body: [
          "All content on this website, including text, graphics, logos, brand names such as KRV Nexus, KRVMart, and KRV FinMart, and their sub-brands, is the property of KRV Nexus Pvt Ltd or its licensors and is protected by applicable intellectual property laws. You may not reproduce or distribute this content without prior written permission.",
        ],
      },
      {
        heading: "4. Business & Forward-Looking Information",
        body: [
          "This website may reference planned stores, future collections, and developing ventures. Such references describe our vision and plans and do not represent current commercial availability or incorporated entities unless expressly stated. Plans are subject to business scale and strategic decisions.",
        ],
      },
      {
        heading: "5. Third-Party Links",
        body: [
          "This website may link to external websites operated by our business verticals or third parties. We are not responsible for the content, policies, or practices of those external sites.",
        ],
      },
      {
        heading: "6. Limitation of Liability",
        body: [
          "The website is provided on an \"as is\" and \"as available\" basis. To the fullest extent permitted by law, KRV Nexus Pvt Ltd shall not be liable for any indirect, incidental, or consequential damages arising from your use of the website.",
        ],
      },
      {
        heading: "7. Governing Law",
        body: [
          "These Terms are governed by and construed in accordance with the laws of India, and any disputes shall be subject to the exclusive jurisdiction of the competent courts in India.",
        ],
      },
    ],
  },

  disclaimer: {
    title: "Disclaimer",
    updated: UPDATED,
    sections: [
      {
        heading: "1. General",
        body: [
          "The information provided on this website is for general informational and corporate communication purposes only. While we strive to keep information accurate and up to date, we make no representations or warranties of any kind about the completeness, accuracy, or reliability of the content.",
        ],
      },
      {
        heading: "2. KRV FinMart — Educational Purpose",
        body: [
          "KRV FinMart provides educational content and market-related information for learning and informational purposes. Nothing published by KRV FinMart should be interpreted as a promise or guarantee of returns, or as personalized investment advice.",
          "Market participation involves risk. Users should evaluate their own circumstances and seek appropriate professional advice where necessary.",
        ],
      },
      {
        heading: "3. No Guaranteed Outcomes",
        body: ["KRV FinMart does not make, and you should not rely on, any claims of:"],
        list: [
          "Guaranteed profit or guaranteed returns.",
          "Fixed monthly income.",
          "Sure-shot calls or risk-free trading.",
        ],
      },
      {
        heading: "4. Regulatory Status",
        body: [
          "Unless expressly stated on this website with the relevant registration details, KRV FinMart does not claim any specific regulatory registration. Any future registrations, if obtained, will be disclosed clearly.",
        ],
      },
      {
        heading: "5. Forward-Looking Statements",
        body: [
          "References to planned stores, future collections, and developing businesses reflect our long-term vision and are subject to change based on business scale and strategic decisions.",
        ],
      },
    ],
  },

  cookies: {
    title: "Cookie Policy",
    updated: UPDATED,
    sections: [
      {
        heading: "1. What Are Cookies",
        body: [
          "Cookies are small text files placed on your device when you visit a website. They help websites function properly, remember preferences, and understand how visitors interact with content.",
        ],
      },
      {
        heading: "2. How We Use Cookies",
        body: ["We may use the following types of cookies:"],
        list: [
          "Essential cookies required for the website to function correctly.",
          "Analytics cookies that help us understand website usage and improve the experience.",
          "Preference cookies that remember your settings and choices.",
        ],
      },
      {
        heading: "3. Managing Cookies",
        body: [
          "Most web browsers allow you to control cookies through their settings. You can choose to block or delete cookies; however, doing so may affect the functionality of parts of this website.",
        ],
      },
      {
        heading: "4. Third-Party Cookies",
        body: [
          "Some cookies may be set by third-party services we use, such as analytics providers. These third parties operate under their own privacy and cookie policies.",
        ],
      },
      {
        heading: "5. Updates",
        body: [
          "We may update this Cookie Policy periodically. Any changes will be reflected on this page with a revised \"last updated\" date.",
        ],
      },
    ],
  },
};
